import { TraceKeyResponseTypeEnum } from '@ulysses-inc/harami_api_client'
import { logEvent as fireBaseLogEvent } from 'firebase/analytics'
import { firebaseAnalytics } from 'src/features/firebase/firebase'
import { TraceKeyConditionFilter } from '../../reports/listBetaFilters/drawer/hooks/useTraceKeyFilter'

/**
 * 回答検索イベントのパラメータ
 */
interface TraceKeySearchEventParams {
  /** レポート検索に利用した検索キーの数 */
  trace_key_count: number
  /** セット選択肢のキー数 */
  response_set_key_count: number
  /** 日時のキー数 */
  date_key_count: number
  /** テキストのキー数 */
  text_key_count: number
  /** 検索条件の詳細（key名 100文字まで） */
  conditions: string
  /** サニタイズされたURL */
  page_location: string
}

/**
 * トレースキー検索イベントをFirebase Analyticsに送信する
 *
 * @param filters - トレースキーの検索条件フィルター配列
 */
export const logTraceKeyFilterEvent = (
  filters: TraceKeyConditionFilter[],
): void => {
  if (!firebaseAnalytics) {
    return
  }
  if (filters.length === 0) {
    return
  }
  const params = buildEventParams(filters)
  fireBaseLogEvent(firebaseAnalytics, 'search_trace_key', params)
}

/**
 * URLからクエリパラメータを削除してサニタイズする
 * @returns サニタイズされたURL（パスのみ）
 */
const sanitizePageLocation = (): string => {
  if (typeof window === 'undefined') {
    return ''
  }
  const url = new URL(window.location.href)
  // オリジンとパスのみを返し、クエリパラメータとハッシュは除外
  return `${url.origin}${url.pathname}`
}

/**
 * フィルター条件を簡潔な文字列形式に変換する
 * 形式: "RESPONSE_SET: キー名", "DATE: 日付値", "TEXT: テキスト値"
 *
 * @param filter - トレースキーの検索条件フィルター
 * @returns 簡潔な文字列表現
 */
const formatFilterCondition = (filter: TraceKeyConditionFilter): string => {
  switch (filter.responseType) {
    case TraceKeyResponseTypeEnum.ResponseSet:
      return `RESPONSE_SET: ${filter.traceKeyName}`
    case TraceKeyResponseTypeEnum.Date:
      return `DATE: ${filter.traceKeyName}`
    case TraceKeyResponseTypeEnum.Text:
      return `TEXT: ${filter.traceKeyName}`
    default: {
      // https://zenn.dev/qnighy/articles/462baa685c80e2
      throw new Error(
        `Unknown responseType: ${(filter as { responseType: '__invalid__' }).responseType}`,
      )
    }
  }
}

/**
 * イベントパラメータを構築する
 *
 * @param filters - トレースキーの検索条件フィルター配列
 * @returns Firebase Analyticsに送信するイベントパラメータ
 */
const buildEventParams = (
  filters: TraceKeyConditionFilter[],
): TraceKeySearchEventParams => {
  return {
    trace_key_count: filters.length,
    response_set_key_count: filters.filter(
      ({ responseType }) =>
        responseType === TraceKeyResponseTypeEnum.ResponseSet,
    ).length,
    date_key_count: filters.filter(
      ({ responseType }) => responseType === TraceKeyResponseTypeEnum.Date,
    ).length,
    text_key_count: filters.filter(
      ({ responseType }) => responseType === TraceKeyResponseTypeEnum.Text,
    ).length,
    conditions: filters.map(formatFilterCondition).join(', '),
    page_location: sanitizePageLocation(),
  }
}
